import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

/**
 * Italian-formatted PDF report for the Gestionale Incassi & Pagamenti
 * module (Gruppo Impresa Suite).
 *
 * Params:
 *   title         page title (e.g. "Scadenze")
 *   subtitle      optional secondary line (e.g. filter summary)
 *   columns       autoTable head config: [{ header, key, type?, align?, width? }]
 *   rows          array of plain objects (values resolved by `key`)
 *   totals        optional { planned, paid, remaining } shown in footer
 *   filename      saved file name (without extension)
 *   orientation   'portrait' | 'landscape'
 */
const formatDateIT = (s) => {
  if (!s) return "";
  if (s instanceof Date) {
    const d = s;
    return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()}`;
  }
  if (typeof s === "string" && /^\d{4}-\d{2}-\d{2}/.test(s)) {
    const [y, m, d] = s.slice(0, 10).split("-");
    return `${d}/${m}/${y}`;
  }
  return String(s);
};

const pad = (n) => String(n).padStart(2, "0");

const formatCurrencyIT = (n) => {
  if (n === null || n === undefined || n === "") return "";
  const num = typeof n === "number" ? n : parseFloat(n);
  if (!Number.isFinite(num)) return String(n);
  const fixed = num.toFixed(2);
  const [intPart, dec] = fixed.split(".");
  const grouped = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ".");
  return `€ ${grouped},${dec}`;
};

const formatCellValue = (raw, type) => {
  if (raw === null || raw === undefined || raw === "") return "—";
  if (type === "date") return formatDateIT(raw);
  if (type === "currency") return formatCurrencyIT(raw);
  if (type === "integer") {
    const n = typeof raw === "number" ? raw : parseFloat(raw);
    return Number.isFinite(n) ? String(Math.round(n)) : String(raw);
  }
  return String(raw);
};

export function exportPdf({
  title,
  subtitle,
  columns,
  rows,
  totals,
  filename = "report",
  orientation = "landscape",
  brand = {
    name: "Gestionale Incassi & Pagamenti",
    tagline: "Gruppo Impresa Suite · Controllo economico",
  },
}) {
  const doc = new jsPDF({
    orientation,
    unit: "pt",
    format: "a4",
  });
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const marginX = 36;

  // ---------- Header ----------
  // Brand
  doc.setFont("helvetica", "bold");
  doc.setFontSize(13);
  doc.setTextColor(15, 17, 21);
  doc.text(brand.name, marginX, 44);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.setTextColor(120, 113, 108);
  doc.text(brand.tagline, marginX, 58);

  // Generated timestamp (right side)
  const now = new Date();
  const stamp = `Generato il ${pad(now.getDate())}/${pad(
    now.getMonth() + 1,
  )}/${now.getFullYear()} alle ${pad(now.getHours())}:${pad(
    now.getMinutes(),
  )}`;
  doc.setFontSize(8);
  doc.text(stamp, pageWidth - marginX, 44, { align: "right" });

  // Section title
  doc.setFont("helvetica", "bold");
  doc.setFontSize(16);
  doc.setTextColor(15, 17, 21);
  doc.text(title, marginX, 92);

  if (subtitle) {
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.setTextColor(87, 83, 78);
    // wrap subtitle if long
    const wrapped = doc.splitTextToSize(subtitle, pageWidth - marginX * 2);
    doc.text(wrapped, marginX, 108);
  }

  // Divider
  doc.setDrawColor(220, 217, 213);
  doc.setLineWidth(0.5);
  const dividerY = subtitle ? 122 : 104;
  doc.line(marginX, dividerY, pageWidth - marginX, dividerY);

  // ---------- Table ----------
  const head = [columns.map((c) => c.header)];
  const body = (rows || []).map((row) =>
    columns.map((c) => formatCellValue(row[c.key], c.type)),
  );

  // Per-column styles (alignment)
  const columnStyles = {};
  columns.forEach((c, i) => {
    const style = {};
    if (c.type === "currency" || c.type === "integer") {
      style.halign = "right";
    } else if (c.align) {
      style.halign = c.align;
    }
    if (c.width) style.cellWidth = c.width;
    columnStyles[i] = style;
  });

  autoTable(doc, {
    startY: dividerY + 14,
    head,
    body,
    margin: { left: marginX, right: marginX },
    styles: {
      font: "helvetica",
      fontSize: 8.5,
      cellPadding: 5,
      lineColor: [233, 231, 226],
      lineWidth: 0.3,
      textColor: [38, 36, 34],
      overflow: "linebreak",
    },
    headStyles: {
      fillColor: [245, 244, 240],
      textColor: [50, 47, 44],
      fontStyle: "bold",
      lineColor: [220, 217, 213],
      lineWidth: 0.4,
    },
    alternateRowStyles: {
      fillColor: [251, 250, 247],
    },
    columnStyles,
    didDrawPage: (data) => {
      // Footer page number
      const str = `Pagina ${doc.getNumberOfPages()}`;
      doc.setFont("helvetica", "normal");
      doc.setFontSize(8);
      doc.setTextColor(150, 145, 140);
      doc.text(str, pageWidth - marginX, pageHeight - 18, { align: "right" });
      doc.text(brand.name, marginX, pageHeight - 18);
    },
  });

  // ---------- Totals footer ----------
  if (totals) {
    let cursorY = doc.lastAutoTable?.finalY ?? dividerY + 14;
    cursorY += 18;
    if (cursorY > pageHeight - 80) {
      doc.addPage();
      cursorY = 60;
    }
    doc.setDrawColor(220, 217, 213);
    doc.setLineWidth(0.4);
    doc.line(marginX, cursorY, pageWidth - marginX, cursorY);
    cursorY += 14;
    doc.setFont("helvetica", "bold");
    doc.setFontSize(10);
    doc.setTextColor(15, 17, 21);
    doc.text("Totali", marginX, cursorY);

    // Right-aligned values
    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    const lineGap = 16;
    const labelX = pageWidth - marginX - 200;
    const valueX = pageWidth - marginX;
    if (totals.planned !== undefined) {
      doc.setTextColor(87, 83, 78);
      doc.text("Totale pianificato", labelX, cursorY);
      doc.setFont("helvetica", "bold");
      doc.setTextColor(15, 17, 21);
      doc.text(formatCurrencyIT(totals.planned), valueX, cursorY, {
        align: "right",
      });
      cursorY += lineGap;
      doc.setFont("helvetica", "normal");
    }
    if (totals.paid !== undefined) {
      doc.setTextColor(87, 83, 78);
      doc.text("Totale pagato", labelX, cursorY);
      doc.setFont("helvetica", "bold");
      doc.setTextColor(4, 120, 87);
      doc.text(formatCurrencyIT(totals.paid), valueX, cursorY, {
        align: "right",
      });
      cursorY += lineGap;
      doc.setFont("helvetica", "normal");
    }
    if (totals.remaining !== undefined) {
      doc.setTextColor(87, 83, 78);
      doc.text("Totale residuo", labelX, cursorY);
      doc.setFont("helvetica", "bold");
      doc.setTextColor(190, 18, 60);
      doc.text(formatCurrencyIT(totals.remaining), valueX, cursorY, {
        align: "right",
      });
      cursorY += lineGap;
    }
    if (typeof totals.count === "number") {
      doc.setFont("helvetica", "normal");
      doc.setFontSize(8);
      doc.setTextColor(120, 113, 108);
      doc.text(
        `${totals.count} occorrenz${totals.count === 1 ? "a" : "e"} nel periodo`,
        marginX,
        cursorY,
      );
    }
  }

  doc.save(`${filename}.pdf`);
}
